import React, { useCallback, useEffect, useRef, useState } from 'react';
import './GpayScratchFoil.css';

const STORAGE_KEY = 'locsy_scratched_coupon_ids';

export function loadScratchedIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

export function markCouponScratched(couponId) {
  if (!couponId) return;
  const ids = loadScratchedIds();
  ids.add(String(couponId));
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));

  // Soft server sync — fail-open so older backends / offline still work
  try {
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    fetch(`${base}/coupons/${couponId}/scratch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: '{}',
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
}

export function isCouponScratched(couponId) {
  return loadScratchedIds().has(String(couponId));
}

/**
 * GPay-style metallic foil scratch layer.
 * Renders over children; finger/mouse erases foil; fires onRevealed at ~32% coverage.
 */
export default function GpayScratchFoil({
  children,
  couponId,
  alreadyRevealed = false,
  discountLabel = 'REWARD',
  onRevealed,
  height = 230,
  className = '',
}) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const scratching = useRef(false);
  const lastPoint = useRef(null);
  const [revealed, setRevealed] = useState(alreadyRevealed);
  const [celebrating, setCelebrating] = useState(false);
  const [confetti, setConfetti] = useState([]);

  const paintFoil = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || revealed) return;

    const rect = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = rect.width;
    const h = rect.height;

    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, '#B8BEC8');
    grad.addColorStop(0.28, '#E8ECF2');
    grad.addColorStop(0.52, '#9AA3B2');
    grad.addColorStop(0.78, '#D5DAE3');
    grad.addColorStop(1, '#ADB5C2');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Speckle
    for (let i = 0; i < 90; i++) {
      ctx.beginPath();
      ctx.fillStyle = `rgba(255,255,255,${0.12 + Math.random() * 0.15})`;
      ctx.arc(Math.random() * w, Math.random() * h, 0.6 + Math.random() * 2.2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = '#3D4554';
    ctx.font = '800 22px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('TAP HERE', w / 2, h / 2 - 8);
    ctx.fillStyle = '#5C6575';
    ctx.font = '500 13px "Segoe UI", system-ui, sans-serif';
    ctx.fillText('Tap to reveal your coupon', w / 2, h / 2 + 18);
  }, [revealed]);

  useEffect(() => {
    setRevealed(alreadyRevealed);
  }, [alreadyRevealed, couponId]);

  useEffect(() => {
    if (revealed) return undefined;
    paintFoil();
    const onResize = () => paintFoil();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [paintFoil, revealed]);

  const coverage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const { width, height } = canvas;
    if (width < 4 || height < 4) return 0;
    const step = 8;
    let transparent = 0;
    let total = 0;
    const data = ctx.getImageData(0, 0, width, height).data;
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const a = data[(y * width + x) * 4 + 3];
        total += 1;
        if (a < 40) transparent += 1;
      }
    }
    return total ? transparent / total : 0;
  };

  const completeReveal = useCallback(() => {
    if (revealed) return;
    setRevealed(true);
    setCelebrating(true);
    markCouponScratched(couponId);

    const pieces = Array.from({ length: 42 }, (_, i) => ({
      id: i,
      left: 35 + Math.random() * 30,
      top: 35 + Math.random() * 20,
      dx: (Math.random() - 0.5) * 220,
      dy: -40 - Math.random() * 160,
      rot: Math.random() * 720,
      color: ['#FF6B00', '#FFB347', '#16A34A', '#3B82F6', '#EC4899', '#FBBF24'][i % 6],
      delay: Math.random() * 0.15,
    }));
    setConfetti(pieces);

    if (typeof navigator?.vibrate === 'function') navigator.vibrate(30);
    onRevealed?.();

    setTimeout(() => setCelebrating(false), 2400);
  }, [couponId, onRevealed, revealed]);

  const eraseAt = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = canvas.getContext('2d');
    // Drawn in CSS pixel space via setTransform in paintFoil
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = 56;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (lastPoint.current) {
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();
    lastPoint.current = { x, y };

    if (coverage() >= 0.32) completeReveal();
  };

  const getPoint = (e) => {
    if (e.touches?.[0]) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const onStart = (e) => {
    if (revealed) return;
    e.preventDefault();
    e.stopPropagation();
    scratching.current = true;
    lastPoint.current = null;
    const p = getPoint(e);
    eraseAt(p.x, p.y);
  };

  const onMove = (e) => {
    if (!scratching.current || revealed) return;
    e.preventDefault();
    e.stopPropagation();
    const p = getPoint(e);
    eraseAt(p.x, p.y);
  };

  const onEnd = (e) => {
    e?.stopPropagation?.();
    scratching.current = false;
    lastPoint.current = null;
    if (!revealed && coverage() >= 0.28) completeReveal();
  };

  return (
    <div
      className={`gpay-scratch-wrap ${className}`}
      ref={wrapRef}
      style={{ height }}
    >
      <div className="gpay-scratch-face">{children}</div>

      {!revealed && (
        <canvas
          ref={canvasRef}
          className="gpay-scratch-foil"
          aria-label={`Reveal ${discountLabel} coupon`}
          onMouseDown={onStart}
          onMouseMove={onMove}
          onMouseUp={onEnd}
          onMouseLeave={onEnd}
          onTouchStart={onStart}
          onTouchMove={onMove}
          onTouchEnd={(e) => { onEnd(e); completeReveal(); }}
          onClick={(e) => { e.stopPropagation(); completeReveal(); }}
        />
      )}

      {celebrating && (
        <>
          <div className="gpay-confetti-layer" aria-hidden>
            {confetti.map((p) => (
              <span
                key={p.id}
                className="gpay-confetti-piece"
                style={{
                  left: `${p.left}%`,
                  top: `${p.top}%`,
                  background: p.color,
                  animationDelay: `${p.delay}s`,
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                  '--rot': `${p.rot}deg`,
                }}
              />
            ))}
          </div>
          <div className="gpay-congrats-burst">
            <div className="gpay-congrats-card">
              <span className="gpay-congrats-icon">🎉</span>
              <h3>Congratulations!</h3>
              <p>You unlocked {discountLabel}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
